import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Toolbar,
  useTheme,
  useMediaQuery
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useNavigate } from 'react-router-dom';
import Footer from '../components/Footer';
import ContactFormSection from '../components/ContactFormSection';
import HeroSectionComponent from '../components/HeroSectionComponent';
import PublicHeader from '../components/PublicHeader'; 
import SEO from '../components/SEO';

const PublicFaqPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('faq');
  const { t: tCommon } = useTranslation('common');
  
  // Get current language
  const currentLang = i18n.language;

  // Get FAQ questions from translations - handle object structure
  const questionsObject = t('questions', { returnObjects: true });
  
  // Convert questions object to array
  const faqList = questionsObject ? Object.keys(questionsObject).map(key => ({
    question: questionsObject[key].question,
    answer: questionsObject[key].answer
  })) : [];

  // Dynamic schema based on language
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqList.map((faq) => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  const faqBreadcrumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "首頁", "item": "https://rebaz.ai" },
      { "@type": "ListItem", "position": 2, "name": t('pageTitle', '常見問題'), "item": "https://rebaz.ai/faq-public" }
    ]
  };

  // Dynamic SEO metadata based on language
  const getSeoTitle = () => {
    const pageTitle = t('pageTitle', '常見問題');
    switch(currentLang) {
      case 'ja':
        return `${pageTitle} | Web Chat・LINE AIエージェント - Rebaz AI`;
      case 'en':
        return `${pageTitle} | Web Chat & LINE AI Agent - Rebaz AI`;
      case 'zh-CN':
        return `${pageTitle} | 网站 Web Chat・LINE AI 代理人 - Rebaz AI`;
      default: // zh-TW
        return `${pageTitle} | 網站 Web Chat・LINE AI 代理人 - Rebaz AI`;
    }
  };

  const getSeoDescription = () => {
    switch(currentLang) {
      case 'ja':
        return 'Rebaz AI Web Chat・LINE AIエージェントシステムに関するよくある質問と回答。AIカスタマーサービス、知識ベース、自動応答について学びましょう。';
      case 'en':
        return 'Frequently asked questions about Rebaz AI Web Chat & LINE AI Agent system. Learn about AI customer service, knowledge base, and automated responses.';
      case 'zh-CN':
        return '有关 Rebaz AI 网站 Web Chat 及 LINE AI 代理人系统的常见问题与解答。了解如何启用 AI 客服、使用知识库、自动回复及提升客户体验。';
      default: // zh-TW
        return '有關 Rebaz AI 網站 Web Chat 及 LINE AI 代理人系統的常見問題與解答。了解如何啟用 AI 客服、使用知識庫、自動回覆及提升客戶體驗。';
    }
  };

  const getSeoKeywords = () => {
    switch(currentLang) {
      case 'ja':
        return 'Rebaz AI, よくある質問, AIカスタマーサービス, Web Chat, LINEボット, 自動応答, AIエージェント';
      case 'en':
        return 'Rebaz AI, FAQ, AI customer service, Web Chat, LINE bot, automated response, AI agent';
      case 'zh-CN':
        return 'Rebaz AI, 常见问题, AI 客服, Web Chat, 网站聊天, Line 机器人, 自动回复, AI 代理人';
      default: // zh-TW
        return 'Rebaz AI, 常見問題, AI 客服, Web Chat, 網站聊天, Line 機器人, 自動回覆, AI 代理人';
    }
  };

  // Handle contact button click (if needed)
  const handleContactClick = () => {
    navigate('/contact');
  };

  return (
    <>
      <SEO
        title={getSeoTitle()}
        description={getSeoDescription()}
        keywords={getSeoKeywords()}
        canonicalUrl="https://rebaz.ai/faq-public"
        image="https://agent-rebaz-ai.s3.ap-northeast-1.amazonaws.com/FAQ_hero_wide.png"
        schema={faqSchema}
        breadcrumb={faqBreadcrumb}
      />
      <Box display="flex" flexDirection="column" minHeight="100vh">
        {/* Header */}
        <PublicHeader/>
        <Toolbar/>
        
        {/* Main Content */}
        <Box component="main" sx={{ flexGrow: 1, backgroundColor: 'white' }}>

          {/* Hero Section */}
          <HeroSectionComponent
            title={t('heroTitle')}
            subtitle={t('heroSubtitle')}
            buttonText={tCommon('buttons.contactUs')}
            imageSrc="https://agent-rebaz-ai.s3.ap-northeast-1.amazonaws.com/FAQ_hero_wide.png"
            showButton={false}
          />
          
          <Container sx={{ py: 8 }}>
            <Typography 
              variant="h3" 
              component="h2" 
              gutterBottom 
              fontWeight="bold" 
              textAlign="center"
            >
              {t('sectionTitle')}
            </Typography>
            <Typography 
              variant="h6" 
              color="text.secondary" 
              textAlign="center" 
              mb={4}
            >
              {t('sectionSubtitle')}
            </Typography>

            {faqList.map((faq, index) => (
              <Accordion
                key={index}
                elevation={0}
                sx={{
                  mb: 2,
                  border: 'none',
                  boxShadow: 'none',
                  '&::before': { display: 'none' }
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography 
                    fontWeight="bold" 
                    fontSize={{ xs: '1.2rem', md: '1.5rem' }}
                    sx={{ pr: 2 }}
                  >
                    {faq.question}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ pl: 2 }}>
                  <Typography 
                    fontSize={{ xs: '1rem', md: '1.2rem' }}
                    sx={{ lineHeight: 1.6 }}
                  >
                    {faq.answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Container>

          {/* Contact Form Section */}
          <ContactFormSection 
            source="faq"
            title={t('contact.title')}
            subtitle={t('contact.subtitle')}
          />
        </Box>

        {/* Footer */}
        <Footer />
      </Box>
    </>
  );
};

export default PublicFaqPage;